<template>
    <b-row>
        <b-col class="pt-2">
            <!--  Page  -->
            <div v-if="user_permissions.includes('read-whatsapp-campaigns')" class="bg-white rounded py-2 shadow-widget border">

                <!-- Page header -->
                <div class="px-3">

                    <div class="d-flex">

                        <div>
                            <h4>{{ $t('simple.whatsapp_campaigns') }}</h4>
                            <div class="text-muted template-header-info"> {{ $t('simple.campaigns_desc') }}</div>
                        </div>

                        <div class="ml-auto align-self-center d-flex">
                          <div v-b-tooltip.hover.right="{
                               title:!user_permissions.includes('create-whatsapp-campaigns')? $t('permission.permission_denied', { permission: 'create-whatsapp-campaigns' }) : '',customClass: 'tooltip-dark' }"
                               style="display: inline-block">
                            <b-button :variant="'info'" :to="{name: 'whatsapp_campaign_setup'}" :class="['btn-sm mr-2 px-3',{ 'cursor-not-allowed': !user_permissions.includes('create-whatsapp-campaigns') }]"
                                      :disabled="!user_permissions.includes('create-whatsapp-campaigns')">
                              <font-awesome-icon :icon="'plus'" />
                              {{ $t('default.add') }}
                            </b-button>
                          </div>

                            <b-button :variant="'outline-info'" @click="refreshWhatsappCampaigns" class="btn-sm">
                                <font-awesome-icon :icon="'sync'" />
                                {{ $t('default.refresh') }}
                            </b-button>

                        </div>

                    </div>

                </div>

                <hr>

                <!-- Page content -->
                <div class="px-2">

                    <b-row>
                        <b-col>
                            <b-pagination hide-goto-end-buttons size="sm"
                                          @input="refreshWhatsappCampaigns" :total-rows="total" :value="current" :per-page="perPage">
                            </b-pagination>
                        </b-col>
                        <b-col md="4" class="my-2 ml-auto">
                            <b-input-group>
                                <b-form-input v-model="search" :placeholder="$t('simple.type_to_search')" />
                                <b-input-group-append>
                                    <b-btn :disabled="!search" @click="search = ''">{{ $t('simple.clear') }}</b-btn>
                                </b-input-group-append>
                            </b-input-group>
                        </b-col>
                    </b-row>

                    <v-wait for="whatsapp_campaigns_action">
                        <loading slot="waiting" />
                    <b-table stacked="md" responsive bordered hover :items="whatsapp_campaigns"
                        :fields="window_width >= 1200 ? whatsapp_campaigns_fields : whatsapp_campaigns_fields_collapsed"
                         class="vh-50">

                        <!-- Account -->
                        <template slot="cell(account_name)" slot-scope="cell">
                            <span v-b-tooltip.hover :title="cell.item.account.username">{{ cell.item.account.name }}
                                <font-awesome-icon :icon="'question-circle'" size="sm" /></span>
                        </template>

                        <template slot="cell(status)" slot-scope="cell">
                             <span
                                :class="{ 'text-danger': cell.item.status === 'processing', 'text-warning': cell.item.status === 'pending' || cell.item.status === 'stopped', 'text-info': cell.item.status === 'scheduled', 'text-success': cell.item.status === 'completed' }">
                                {{ cell.item.status.toUpperCase() }}
                            </span>
                          <font-awesome-icon
                              v-if="cell.item.scheduled_at && cell.item.status === 'scheduled'"
                              style="font-size:13px" icon="exclamation-circle" class="text-info"
                              v-b-tooltip.hover.right="$t('simple.campaign_scheduled_for', { date: $moment($moment.utc(cell.item.scheduled_at)).local().format('YYYY-MM-DD HH:mm:ss')})" />
                          <font-awesome-icon
                              v-if="cell.item.media_status === 'pending'"
                              style="font-size:13px" icon="clock" class="text-warning"
                              v-b-tooltip.hover.right="{ title: $t('simple.campaign_media_pending'), customClass: 'tooltip-dark' }" />
                          <font-awesome-icon
                              v-if="cell.item.media_status === 'failed'"
                              style="font-size:13px" icon="exclamation-triangle" class="text-danger"
                              v-b-tooltip.hover.right="{ title: cell.item.media_error || $t('simple.campaign_media_failed'), customClass: 'tooltip-dark' }" />
                        </template>


                        <template slot="cell(actions)" slot-scope="cell">
                            <b-button size="sm" @click="cell.toggleDetails()" v-if="window_width < 1200">
                                {{ cell.detailsShowing ? 'Hide' : 'Show' }} Details
                            </b-button>

                            <b-dropdown right :text="$t('simple.actions')" size="sm">

                                <template v-if="cell.item.status === 'pending' || cell.item.status === 'scheduled'">

                                    <template v-if="cell.item.whatsapp_template && cell.item.customer_total > 0 ">
                                        <b-dropdown-item @click="whatsappCampaignStart(cell.item.account.id, cell.item.id)" :class="!user_permissions.includes('update-whatsapp-campaigns') ? 'cursor-not-allowed' : ''"
                                        v-b-tooltip.hover.right="{ title: !user_permissions.includes('update-whatsapp-campaigns') ? $t('permission.permission_denied', { permission: 'update-whatsapp-campaigns' }) :
                                        (cell.item.scheduled_at ? $t('simple.campaign_scheduled', { date: $moment($moment.utc(cell.item.scheduled_at)).local().format('YYYY-MM-DD HH:mm:ss')}): ''), customClass: 'tooltip-dark' }"
                                        :disabled="!user_permissions.includes('update-whatsapp-campaigns') || cell.item.scheduled_at">{{$t('simple.start_campaign') }}</b-dropdown-item>
                                    </template>

                                    <b-dropdown-item @click="whatsappCampaignEdit(cell.item)" :class="(!user_permissions.includes('update-whatsapp-campaigns') || !user_permissions.includes('read-template-messages')) ? 'cursor-not-allowed' : ''"
                                       v-b-tooltip.hover.right="{ title: (!user_permissions.includes('update-whatsapp-campaigns') || !user_permissions.includes('read-template-messages')) ? $t('permission.permission_denied', { permission: 'update-whatsapp-campaigns | read-template-messages' }) : '', customClass: 'tooltip-dark' }"
                                       :disabled="(!user_permissions.includes('update-whatsapp-campaigns') || !user_permissions.includes('read-template-messages'))">{{$t('simple.edit_campaign') }}</b-dropdown-item>

                                    <b-dropdown-item @click="deleteCampaign(cell.item.account.id, cell.item.id)" :class="!user_permissions.includes('delete-whatsapp-campaigns') ? 'cursor-not-allowed' : ''"
                                       v-b-tooltip.hover.right="{ title: !user_permissions.includes('delete-whatsapp-campaigns') ? $t('permission.permission_denied', { permission: 'delete-whatsapp-campaigns' }) : '', customClass: 'tooltip-dark' }"
                                       :disabled="!user_permissions.includes('delete-whatsapp-campaigns')">{{$t('simple.delete_campaign') }}</b-dropdown-item>

                                </template>

                                <template v-else>

                                    <b-dropdown-item
                                    @click="whatsappCampaignDetails(cell.item)">{{$t('simple.campaign_details') }}</b-dropdown-item>

                                    <b-dropdown-item v-if="cell.item.status === 'processing' && cell.item.media_status === 'failed'"
                                       @click="retryCampaignMedia(cell.item.account.id, cell.item.id)"
                                       :class="!user_permissions.includes('update-whatsapp-campaigns') ? 'cursor-not-allowed' : ''"
                                       v-b-tooltip.hover.right="{ title: !user_permissions.includes('update-whatsapp-campaigns') ? $t('permission.permission_denied', { permission: 'update-whatsapp-campaigns' }) : '', customClass: 'tooltip-dark' }"
                                       :disabled="!user_permissions.includes('update-whatsapp-campaigns')">{{$t('simple.retry_media_upload') }}</b-dropdown-item>

                                    <b-dropdown-item v-if="cell.item.status === 'processing'"
                                       @click="confirmStopCampaign(cell.item)"
                                       :class="['text-danger', !user_permissions.includes('update-whatsapp-campaigns') ? 'cursor-not-allowed' : '']"
                                       v-b-tooltip.hover.right="{ title: !user_permissions.includes('update-whatsapp-campaigns') ? $t('permission.permission_denied', { permission: 'update-whatsapp-campaigns' }) : '', customClass: 'tooltip-dark' }"
                                       :disabled="!user_permissions.includes('update-whatsapp-campaigns')">{{$t('simple.force_stop_campaign') }}</b-dropdown-item>

                                    <b-dropdown-item v-if="cell.item.status === 'stopped'"
                                       @click="resumeCampaign(cell.item.account.id, cell.item.id)"
                                       :class="!user_permissions.includes('update-whatsapp-campaigns') ? 'cursor-not-allowed' : ''"
                                       v-b-tooltip.hover.right="{ title: !user_permissions.includes('update-whatsapp-campaigns') ? $t('permission.permission_denied', { permission: 'update-whatsapp-campaigns' }) : '', customClass: 'tooltip-dark' }"
                                       :disabled="!user_permissions.includes('update-whatsapp-campaigns')">{{$t('simple.resume_campaign') }}</b-dropdown-item>

                                </template>

                                    

                            </b-dropdown>
                        </template>

                        <template #row-details="row">
                            <b-table small stacked="md" class="d-xl-none" responsive bordered hover :items="[row.item]"
                                :fields="whatsapp_campaigns_fields_not_collapsed">
                                <!-- Created at -->
                                <template slot="cell(created_at)" slot-scope="cell">

                                    {{ $moment($moment.utc(cell.item.created_at)).local().format('YYYY-MM-DD HH:mm') }}

                                </template>

                                <!-- Updated at -->
                                <template slot="cell(updated_at)" slot-scope="cell">

                                    {{ $moment($moment.utc(cell.item.updated_at)).local().format('YYYY-MM-DD HH:mm') }}

                                </template>

                            </b-table>
                        </template>

                        <!-- Created at -->
                        <template slot="cell(created_at)" slot-scope="cell">

                            {{ $moment($moment.utc(cell.item.created_at)).local().format('YYYY-MM-DD HH:mm') }}

                        </template>

                        <!-- Updated at -->
                        <template slot="cell(updated_at)" slot-scope="cell">

                            {{ $moment($moment.utc(cell.item.updated_at)).local().format('YYYY-MM-DD HH:mm') }}

                        </template>

                        <template slot="cell(type)" slot-scope="cell">

                            {{ cell.item.whatsapp_template ?  cell.item.whatsapp_template.template_type : '-'}}
                                                
                        </template>

                    </b-table>

                    </v-wait>


                    <v-wait for="whatsapp_campaigns">
                        <loading slot="waiting" />
                    </v-wait>

                </div>


            </div>
            <div class="alert alert-danger text-center mt-5" style="max-width: 50%; margin: 0 auto;" v-if="permissionsReady && !user_permissions.includes('read-whatsapp-campaigns')">
              {{ $t('permission.permission_denied',{ permission: 'read-whatsapp-campaigns'}) }}
            </div>
        </b-col>

    </b-row>
</template>
<script>
import { mapActions, mapGetters } from "vuex";

export default {
    metaInfo: function () {
        return {
            title: 'Whatsapp Campaigns',
        }
    },

    async mounted() {
        await this.fetchWhatsappCampaigns({page:1})
        this.$nextTick(() => {
            window.addEventListener('resize', this.onResize);
        })
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
    },

    data() {
        return {
            window_width: window.innerWidth,
            whatsapp_campaigns_fields: [
                { key: 'account_name', label: this.$t('simple.account'), sortable: true },
                { key: 'name', label: this.$t('simple.name'), sortable: true },
                { key: 'type', label: this.$t('simple.type'), sortable: true },
                { key: 'status', label: this.$t('simple.status'), sortable: true },
                { key: 'customer_total', label: this.$t('simple.recipients'), sortable: true },
                { key: 'customer_sent', label: this.$t('simple.sent'), sortable: true },
                { key: 'customer_deliver', label: this.$t('simple.delivered'), sortable: true },
                { key: 'customer_read', label: this.$t('simple.read'), sortable: true },
                { key: 'created_at', label: this.$t('simple.created'), sortable: true },
                { key: 'actions', label: this.$t('simple.actions') },
            ],

            whatsapp_campaigns_fields_collapsed: [
                { key: 'account_name', label: this.$t('simple.account'), sortable: true },
                { key: 'name', label: this.$t('simple.name'), sortable: true },
                { key: 'type', label: this.$t('macros.template_type'), sortable: true },
                { key: 'status', label: this.$t('simple.status'), sortable: true },
                { key: 'customer_total', label: this.$t('simple.recipients'), sortable: true },
                { key: 'actions', label: this.$t('simple.actions') },
            ],

            whatsapp_campaigns_fields_not_collapsed: [
                { key: 'customer_sent', label: this.$t('simple.sent'), sortable: true },
                { key: 'customer_deliver', label: this.$t('simple.delivered'), sortable: true },
                { key: 'customer_read', label: this.$t('simple.read'), sortable: true },
                { key: 'created_at', label: this.$t('simple.created'), sortable: true },
            ],
            
        }
    },

    methods: {

        ...mapActions('Templates', [
            'fetchWhatsappCampaigns',
            'deleteWhatsappCampaign',
            'startWhatsappCampaign',
            'retryWhatsappCampaignMedia',
            'stopWhatsappCampaign',
            'resumeWhatsappCampaign',
            'updateSearchWhatsappCampaigns'
        ]),

        onResize() {
            this.window_width = window.innerWidth;
        },

        async refreshWhatsappCampaigns(data) {
            await this.fetchWhatsappCampaigns({ page: Number.isInteger(data) ? data : 1, search: this.getSearchWhatsappCampaignUsers })
        },

        async whatsappCampaignEdit(data) {
            await this.$router.push({ name: 'whatsapp_campaign_edit', params: { account_id: data.account.id, campaign_id: data.id } })
        },

        async whatsappCampaignDetails(data) {
            await this.$router.push({ name: 'whatsapp_campaign_details', params: { account_id: data.account.id, campaign_id: data.id } })
        },

        async deleteCampaign(account_id, campaign_id) {
            await this.deleteWhatsappCampaign({ account_id: account_id, campaign_id: campaign_id })
                .then(() => {
                    this.refreshWhatsappCampaigns()
                })
        },

        async whatsappCampaignStart(account_id, campaign_id) {
            await this.startWhatsappCampaign({ account_id: account_id, campaign_id: campaign_id })
                .then(() => {
                    this.refreshWhatsappCampaigns()
                })
        },

        async retryCampaignMedia(account_id, campaign_id) {
            await this.retryWhatsappCampaignMedia({ account_id: account_id, campaign_id: campaign_id })
                .then(() => {
                    this.refreshWhatsappCampaigns()
                })
        },

        async confirmStopCampaign(item) {
            const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('simple.force_stop_campaign_warning'), {
                title: this.$t('simple.force_stop_campaign'),
                okVariant: 'danger',
                okTitle: this.$t('simple.force_stop_campaign'),
                cancelTitle: this.$t('default.cancel'),
                centered: true,
            })
            if (!confirmed) {
                return
            }
            await this.stopWhatsappCampaign({ account_id: item.account.id, campaign_id: item.id })
                .then(() => {
                    this.refreshWhatsappCampaigns()
                })
        },

        async resumeCampaign(account_id, campaign_id) {
            await this.resumeWhatsappCampaign({ account_id: account_id, campaign_id: campaign_id })
                .then(() => {
                    this.refreshWhatsappCampaigns()
                })
        },

    },

    computed: {
        ...mapGetters({
            accounts: 'Accounts/getAccounts',
            whatsapp_campaigns: 'Templates/getWhatsappCampaigns',
            getSearchWhatsappCampaigns: 'Templates/getSearchWhatsappCampaigns',
            user_permissions: 'User/getUserPermissions',
            total: 'Templates/getCampaignTotal',
            current: 'Templates/getCampaignCurrent',
            perPage: 'Templates/getCampaignPerPage',
        }),

        search:{
            get(){
                return this.getSearchWhatsappCampaigns;
            },
            set(newSearch) {
                if (this.getSearchWhatsappCampaigns !== newSearch) {
                    this.updateSearchWhatsappCampaigns(newSearch)
                    this.fetchWhatsappCampaigns({
                        page: 1,
                        search: newSearch,
                    });
                }
            }
        },

        whatsapp_accounts() {
            return this.accounts.filter(account => account.integration_id === 'whatsapp')
        },

        permissionsReady() {
            return Array.isArray(this.user_permissions) && this.user_permissions.length > 0;
        }
    },
}
</script>
